package com.sddamico.draftmachine.util

import com.sddamico.draftmachine.models.PlayerResponse

/**
 * Relevance search over a resolved player pool.
 *
 * The query is split into tokens on whitespace and commas, and every token must land somewhere on a
 * player — its name, position, or team — for that player to survive. That is what lets "rb atl" and
 * "bijan atl" work at all. Tokens are scored by how cleanly they hit (exact > prefix > word-prefix >
 * substring), and a player's score is the sum across tokens, so the more specific a query gets the
 * further the intended player pulls ahead.
 *
 * Subsequence matching ("bijn" -> "bijan") is a *fallback*, never merged into the literal results:
 * once anything matches literally, loose matches would only add noise.
 *
 * The index pre-normalizes every player's three searchable fields once at construction time so that
 * repeated keystrokes against a ~2000-player pool avoid re-running regex-heavy normalization.
 */

private val TOKEN_SPLIT = Regex("[\\s,]+")
private val NON_ALPHANUMERIC = Regex("[^a-z0-9 ]")
private val WHITESPACE_RUN = Regex("\\s+")

/** Lowercase, drop punctuation, collapse whitespace: "Ja'Marr Chase" -> "jamarr chase". */
private fun normalize(s: String): String =
  s.lowercase().replace(NON_ALPHANUMERIC, "").replace(WHITESPACE_RUN, " ").trim()

private fun tokenize(query: String): List<String> =
  query.split(TOKEN_SPLIT).map { normalize(it) }.filter { it.isNotBlank() }

/** True when every char of [token] appears in [field] in order (gaps allowed). */
private fun isSubsequence(token: String, field: String): Boolean {
  var i = 0
  for (c in field) {
    if (i < token.length && token[i] == c) i++
    if (i == token.length) return true
  }
  return i == token.length
}

private const val SCORE_EXACT = 100
private const val SCORE_PREFIX = 80
private const val SCORE_WORD_PREFIX = 70
private const val SCORE_CONTAINS = 50
private const val SCORE_SUBSEQUENCE = 20

/** Literal hits only. */
private const val THRESHOLD_LITERAL = SCORE_CONTAINS

/** Loose hits allowed — only ever applied when the literal pass came back empty. */
private const val THRESHOLD_FUZZY = SCORE_SUBSEQUENCE

private fun scoreToken(token: String, field: String): Int = when {
  field.isEmpty() -> 0
  field == token -> SCORE_EXACT
  field.startsWith(token) -> SCORE_PREFIX
  field.split(' ').any { it.startsWith(token) } -> SCORE_WORD_PREFIX
  field.contains(token) -> SCORE_CONTAINS
  token.length >= 3 && isSubsequence(token, field) -> SCORE_SUBSEQUENCE
  else -> 0
}

/** A player with its searchable fields normalized once at index construction, not once per search. */
private class Candidate(val player: PlayerResponse, val fields: List<String>)

private fun scoreToken(token: String, candidate: Candidate): Int =
  candidate.fields.maxOf { scoreToken(token, it) }

/**
 * Pre-computed search index over a player pool.
 *
 * Normalizes every player's searchable fields once at construction so that repeated [search] calls
 * (one per keystroke) reuse the work rather than re-running regex-heavy transforms.
 *
 * Both `team` and `teamShortName` are indexed. For NCAAF, `team` holds the full name ("Oklahoma
 * Sooners") while `teamShortName` holds the code ("OKLA") — and `playerTeamLabel()` renders the
 * short one, so the code is what a manager sees on the row and is most likely to type.
 *
 * Ties break toward the player most likely to be drafted: ADP ascending (unranked players last),
 * then name.
 */
class PlayerSearchIndex(players: List<PlayerResponse>) {
  private val candidates: List<Candidate> = players.map { p ->
    Candidate(
      p,
      listOf(
        normalize(p.name),
        normalize(p.position.orEmpty()),
        normalize(p.team.orEmpty()),
        normalize(p.teamShortName.orEmpty()),
      ),
    )
  }

  fun search(query: String, limit: Int = 8): List<PlayerResponse> {
    val tokens = tokenize(query)
    if (tokens.isEmpty() || tokens.joinToString(" ").length < 2) return emptyList()

    fun qualifying(threshold: Int): List<Pair<Candidate, Int>> = candidates.mapNotNull { c ->
      var total = 0
      for (token in tokens) {
        val score = scoreToken(token, c)
        if (score < threshold) return@mapNotNull null
        total += score
      }
      c to total
    }

    val matched = qualifying(THRESHOLD_LITERAL).ifEmpty { qualifying(THRESHOLD_FUZZY) }

    return matched
      .sortedWith(
        compareByDescending<Pair<Candidate, Int>> { it.second }
          .thenBy { it.first.player.adp ?: it.first.player.adpPpr ?: Double.MAX_VALUE }
          .thenBy { it.first.player.name.lowercase() }
      )
      .take(limit)
      .map { it.first.player }
  }
}
